import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, AlertTriangle, X } from "lucide-react";
import { useSubscription, getTrialDaysLeft } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation as useLocationContext } from "@/contexts/LocationContext";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "@/contexts/ToastContext";

/**
 * Banner global de conversión.
 *
 * Muestra dos estados a lo largo de todo el dashboard:
 *  - En prueba: cuenta atrás de días + CTA para activar el plan.
 *  - Prueba terminada (plan Free): aviso persistente para reactivar el plan.
 *
 * No aparece en la propia página de suscripción ni para cuentas con plan de pago.
 */
export default function TrialBanner() {
  const { features, isLoading, refresh } = useSubscription();
  const { showToast } = useGlobalToast();
  const { user } = useAuth();
  const { currentBookingMode } = useLocationContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [undoingCancel, setUndoingCancel] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "owner";

  // No mostrar mientras carga, en la página de suscripción, o sin datos
  if (isLoading || !features || location.pathname === "/subscription") {
    return null;
  }

  const {
    isInTrial,
    trialEndsAt,
    planName,
    planPrice,
    hadPaidSubscription,
    cancelAtPeriodEnd,
    currentPeriodEnd,
  } = features;

  // "el mapa de mesas" / "la agenda de citas": nombrar lo que el negocio usa.
  const funcionDelModo =
    currentBookingMode === "appointments"
      ? "la agenda de citas"
      : "el mapa de mesas";

  // Ex-suscriptor caído a Free (tuvo una suscripción de pago real): mensaje y
  // CTA propios — hablarle de "tu prueba gratuita" es erróneo y frío (caso
  // Botarate Beach, 9-sep-2026). Su botón va DIRECTO al pago del plan que
  // tenía (POST /stripe/reactivate-checkout), sin pasar por la página de
  // planes.
  const lapsedSubscriber = hadPaidSubscription;

  // ── Cancelación programada (por cualquier vía: botón Cancelar, portal de
  // Stripe o "Cambiar a Free") con el plan de pago AÚN VIVO: banner propio.
  // Avisa hasta cuándo conserva sus funciones y ofrece deshacerla en un clic
  // (POST /stripe/reactivate-scheduled). Al llegar la fecha, el webhook lo
  // baja a Free y entonces aplica el banner de ex-suscriptor.
  if (cancelAtPeriodEnd && planPrice > 0) {
    const endDateLabel = currentPeriodEnd
      ? new Date(currentPeriodEnd).toLocaleDateString("es-ES", {
          day: "numeric",
          month: "long",
          timeZone: "Europe/Madrid",
        })
      : null;

    const cancelDismissKey = "rm_cancel_banner_dismissed";
    const todayKey = new Date().toISOString().slice(0, 10);
    const cancelDismissedToday =
      typeof window !== "undefined" &&
      localStorage.getItem(cancelDismissKey) === todayKey;
    if (dismissed || cancelDismissedToday) return null;

    const undoCancellation = async () => {
      if (undoingCancel) return;
      setUndoingCancel(true);
      try {
        const res = await apiClient.post("/stripe/reactivate-scheduled");
        showToast(
          res.data?.message || "Tu plan seguirá renovándose con normalidad.",
          "success",
        );
        await refresh();
      } catch (err) {
        const e = err as { response?: { data?: { message?: string } } };
        showToast(
          e.response?.data?.message || "No se pudo reactivar la renovación",
          "error",
        );
      } finally {
        setUndoingCancel(false);
      }
    };

    return (
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border px-4 py-3 bg-amber-50 border-amber-300 text-amber-900">
        <div className="flex items-start gap-2 flex-1">
          <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
          <p className="text-sm">
            <strong>Tu suscripción no se renovará.</strong> Mantendrás todas
            las funciones de tu plan {planName}
            {endDateLabel ? (
              <>
                {" "}
                hasta el <strong>{endDateLabel}</strong>
              </>
            ) : (
              " hasta el final del periodo ya pagado"
            )}
            ; después pasarás al plan Free y no se te volverá a cobrar.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <button
              onClick={undoCancellation}
              disabled={undoingCancel}
              className="rounded-lg px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
            >
              {undoingCancel
                ? "Reactivando..."
                : `Seguir con mi plan ${planName}`}
            </button>
          )}
          <button
            onClick={() => {
              localStorage.setItem(cancelDismissKey, todayKey);
              setDismissed(true);
            }}
            className="p-1.5 rounded hover:bg-black/5"
            title="Ocultar por hoy"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Días restantes por fecha de calendario en España (estable durante el día,
  // baja a medianoche española). Ver getTrialDaysLeft en useSubscription.
  const daysLeft = getTrialDaysLeft(trialEndsAt);

  // Prueba terminada y de vuelta en Free (tuvo trial pero no contrató)
  const trialEndedOnFree =
    !isInTrial &&
    planName === "Free" &&
    !!trialEndsAt &&
    new Date(trialEndsAt).getTime() < Date.now();

  // Cuenta con plan de pago activo → sin banner
  if (!isInTrial && !trialEndedOnFree) {
    return null;
  }

  const isUrgent = trialEndedOnFree || daysLeft <= 2;

  // Permitir descartar solo si NO es urgente (prueba con holgura)
  const dismissKey = "rm_trial_banner_dismissed";
  const today = new Date().toISOString().slice(0, 10);
  const alreadyDismissedToday =
    typeof window !== "undefined" &&
    localStorage.getItem(dismissKey) === today;
  if (!isUrgent && (dismissed || alreadyDismissedToday)) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(dismissKey, today);
    setDismissed(true);
  };

  // Directo al checkout de Stripe del plan que tenía; si el backend no puede
  // resolverlo, cae a la página de planes (nunca deja al usuario sin camino).
  const goToReactivation = async () => {
    if (reactivating) return;
    setReactivating(true);
    try {
      const res = await apiClient.post("/stripe/reactivate-checkout");
      const url = res.data?.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      navigate("/subscription");
    } catch {
      navigate("/subscription");
    } finally {
      setReactivating(false);
    }
  };

  const goToPlans = () => {
    const intended = localStorage.getItem("rm_intended_plan");
    navigate(intended ? `/subscription?plan=${intended}` : "/subscription");
  };

  const colorClasses = isUrgent
    ? "bg-amber-50 border-amber-300 text-amber-900"
    : "bg-primary-50 border-primary-200 text-primary-900";

  return (
    <div
      className={`mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border px-4 py-3 ${colorClasses}`}
    >
      <div className="flex items-start gap-2 flex-1 min-w-0">
        {isUrgent ? (
          <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
        ) : (
          <Sparkles className="h-5 w-5 mt-0.5 shrink-0" />
        )}
        <p className="text-sm">
          {trialEndedOnFree && lapsedSubscriber ? (
            <>
              <strong>Tu suscripción ya no está activa.</strong> Al no
              renovarse, tu cuenta ha pasado al plan Free: se han desactivado
              WhatsApp, {funcionDelModo} y las reservas ilimitadas. Reactívala
              y lo recuperas todo al momento.
            </>
          ) : trialEndedOnFree ? (
            <>
              <strong>Tu prueba gratuita ha terminado.</strong> Estás en el plan
              Free con funciones limitadas. Reactiva tu plan y recupera WhatsApp,{" "}
              {funcionDelModo} y las reservas ilimitadas.
            </>
          ) : daysLeft <= 2 ? (
            <>
              <strong>
                {daysLeft === 0
                  ? "Tu prueba termina hoy."
                  : `Te queda${daysLeft === 1 ? "" : "n"} ${daysLeft} día${
                      daysLeft === 1 ? "" : "s"
                    } de prueba.`}
              </strong>{" "}
              Activa tu plan ahora para no perder ninguna función.
            </>
          ) : (
            <>
              Estás en tu <strong>prueba gratuita</strong> con las funciones
              premium desbloqueadas
              —{" "}
              <strong>
                {daysLeft} día{daysLeft === 1 ? "" : "s"} restante
                {daysLeft === 1 ? "" : "s"}
              </strong>
              . Activa tu plan cuando quieras y mantén las funciones premium.
            </>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {isAdmin && (
          <button
            onClick={
              trialEndedOnFree && lapsedSubscriber ? goToReactivation : goToPlans
            }
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
              isUrgent
                ? "bg-amber-600 hover:bg-amber-700"
                : "bg-primary-600 hover:bg-primary-700"
            }`}
          >
            {trialEndedOnFree && lapsedSubscriber
              ? reactivating
                ? "Abriendo el pago..."
                : "Reactivar suscripción"
              : trialEndedOnFree
                ? "Reactivar plan"
                : "Ver planes"}
          </button>
        )}
        {!isUrgent && (
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded hover:bg-black/5"
            title="Ocultar por hoy"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
